import { AffiliateEarnings, ShowHeader } from "@/components";
import { ReplenishInvites, UpdatePaymentSettings } from "@/modals";
import { useGetUser, useGetUserInvites } from "@/queries";
import { useUIStore } from "@/stores";
import { ReferralPaymentStatus } from "@/types";
import {formatUserName, formatUserRoleToName, ImgSrc} from "@/utils";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { Loading } from "react-admin";
import { useParams } from "react-router-dom";
import { SetPaymentSettings } from "../../modals";
import { useGetReferralUser } from "../../queries";
import { PaymentSettings } from "@/components";
import { ReferralInvites } from "../referral-invites";

const ReferralsShow: FC = () => {
  const { id } = useParams();
  const { openModal } = useUIStore();

  const { referral, isReferralLoading } = useGetReferralUser(id ?? "");
  const { user, isUserLoading } = useGetUser(id ?? "");
  const { freeInvites, freeInvitesError, isUserInvitesLoading } =
    useGetUserInvites(id ?? "");

  if (
    isReferralLoading ||
    isUserLoading ||
    isUserInvitesLoading ||
    !user ||
    !referral
  ) {
    return <Loading />;
  }

  const isPaidReferral =
    id && referral.paymentStatus === ReferralPaymentStatus.PAID;

  return (
    <Stack direction={"column"} spacing={1} p={2}>
      <ShowHeader
        title="Referral Users"
        rightAddon={
          <Stack direction={"row"} spacing={2}>
            <Stack>
              <Typography variant="body2" color="gray">
                Invite Balance
              </Typography>
              <Stack direction="row" spacing={2}>
                <Typography style={{ whiteSpace: "nowrap" }}>
                  Used: <strong>{freeInvitesError ? "—" : freeInvites}</strong>
                </Typography>
                <Typography style={{ whiteSpace: "nowrap" }}>
                  Left: <strong>{user.remainingInvitesCount}</strong>
                </Typography>
              </Stack>
            </Stack>
            <Button
              variant="contained"
              color="secondary"
              onClick={() =>
                openModal(ReplenishInvites, {
                  title: "Replenish Invites",
                  userId: id ?? "",
                })
              }
            >
              Replenish Invites
            </Button>
          </Stack>
        }
      />

      <Stack spacing={4}>
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Box display="flex" gap={2}>
            <Avatar
              src={ImgSrc(user.profileImage?.path ?? "")}
              alt="Profile Image"
              sx={{ width: 56, height: 56 }}
            />
            <Stack direction={"column"}>
              <Typography variant="h6" fontWeight="bold">
                {formatUserName(user?.firstName, user?.lastName, user.status)}
              </Typography>
              <Typography variant="subtitle1">
                {formatUserRoleToName(user.role)}
              </Typography>
            </Stack>
          </Box>

          {referral.paymentStatus === ReferralPaymentStatus.UNPAID ? (
            <Button
              style={{ height: "fit-content" }}
              variant="contained"
              color="primary"
              onClick={() =>
                referral.id &&
                openModal(SetPaymentSettings, {
                  title: "Set Payment Settings",
                  referralId: referral.id,
                })
              }
            >
              Set Payment
            </Button>
          ) : (
            <Button
              style={{ height: "fit-content" }}
              variant="contained"
              color="primary"
              onClick={() =>
                referral.affiliate &&
                openModal(UpdatePaymentSettings, {
                  title: "Update Payment Settings",
                  affiliateId: referral.affiliate.id,
                  referralId: referral.id,
                  apiValues: {
                    alphaReferralPaymentBps: (
                      referral.affiliate.alphaReferralPaymentBps / 100
                    ).toString(),
                    museReferralPaymentCents: (
                      referral.affiliate.museReferralPaymentCents / 100
                    ).toString(),
                    bankAccountCredentials:
                      referral.affiliate.bankAccountCredentials,
                  },
                })
              }
            >
              Update
            </Button>
          )}
        </Stack>

        {isPaidReferral && (
          <PaymentSettings
            alphaReferralPaymentBps={
              referral.affiliate?.alphaReferralPaymentBps
            }
            museReferralPaymentCents={
              referral.affiliate?.museReferralPaymentCents
            }
            bankAccountCredentials={referral.affiliate?.bankAccountCredentials}
          />
        )}
        <ReferralInvites />

        {isPaidReferral && referral?.affiliate?.id && (
          <AffiliateEarnings affiliateId={referral?.affiliate?.id} />
        )}
      </Stack>
    </Stack>
  );
};

export { ReferralsShow };
